import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { useScorer } from '../lib/useScorer';
import { ContactRow, relTime, tierColor, gradeColor, tierGradeConflict } from '../lib/types';
import { Alert, Badge, Button, EmptyState, Field, Input, Loading, Modal } from '../components/ui';
import { ImportContacts } from '../components/ImportContacts';
import { ContactControls, ContactFilters, DEFAULT_FILTERS, filtersToQuery } from '../components/ContactControls';
import { ScoreControls } from '../components/ScoreControls';
import { WorkStatus } from '../components/WorkStatus';
import { StageControl } from '../components/StageControl';
import { Stage } from '../lib/types';

const PAGE = 100;

interface ContactsResp { contacts: ContactRow[]; total: number; unscored: number; batchName: string | null; categories: string[] }

export default function Contacts() {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const batchId = params.get('batch'); // batch view (Phase 11) — refresh-safe URL param
  const canScore = !!me?.entitlements?.includes('lead_finder');

  // Locked view (Phase 11): restore the user's saved filter/sort setup on load.
  const lockKey = me ? `mtm.contacts.lockedView.${me.user.id}` : '';
  const persisted = useMemo(() => {
    if (!lockKey) return null;
    try { const raw = localStorage.getItem(lockKey); return raw ? JSON.parse(raw) : null; } catch { return null; }
  }, [lockKey]);
  const [locked, setLocked] = useState<boolean>(!!persisted);

  const [q, setQ] = useState<string>(persisted?.q || '');
  const [filters, setFilters] = useState<ContactFilters>(persisted?.filters ? { ...DEFAULT_FILTERS, ...persisted.filters } : DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // While locked, keep the saved view in sync with any change the user makes.
  useEffect(() => {
    if (lockKey && locked) localStorage.setItem(lockKey, JSON.stringify({ filters, q }));
  }, [locked, filters, q, lockKey]);

  function toggleLock() {
    if (locked) { setLocked(false); if (lockKey) localStorage.removeItem(lockKey); }
    else { setLocked(true); if (lockKey) localStorage.setItem(lockKey, JSON.stringify({ filters, q })); }
  }

  const batchParam = batchId ? `&batch=${batchId}` : '';
  const { data, loading, error, reload } = useAsync<ContactsResp>(
    () => apiGet<ContactsResp>(
      `/api/contacts?limit=${PAGE}&offset=${page * PAGE}${q ? `&q=${encodeURIComponent(q)}` : ''}&${filtersToQuery(filters)}${batchParam}`
    ),
    [q, filters, page, batchId]
  );

  // Stages power the inline per-row stage selector + the stage filter (Phase 10:
  // pipeline tracking lives on Contacts, not a separate board).
  const stagesQ = useAsync(() => apiGet<{ stages: Stage[] }>('/api/stages'), []);
  const stages = stagesQ.data?.stages || [];

  // Scoring (Phase 11) — same engine as Lead Score, scoped to the current view
  // (batch + filters). Live overrides update rows instantly as scores stream in.
  const [liveScores, setLiveScores] = useState<Record<number, { score: number; tier: string }>>({});
  const scorer = useScorer((results) => {
    setLiveScores((m) => { const n = { ...m }; results.forEach((r) => { if (r.tier) n[r.id] = { score: r.score, tier: r.tier }; }); return n; });
  });
  const scope = { batch: batchId || undefined, tier: filters.tier || undefined, category: filters.category || undefined, researched: filters.researched || undefined, workStatus: filters.workStatus || undefined, stage: filters.stage || undefined, q: q || undefined };
  async function runScore(mode: 'all' | number) { await scorer.scoreScope(scope, mode); reload(); }
  async function scoreRow(id: number) { await scorer.scoreOne(id); }

  function clearBatch() { params.delete('batch'); setParams(params, { replace: true }); }

  // Reset to first page whenever the query/filters/batch change.
  useEffect(() => { setPage(0); }, [q, filters, batchId]);

  // Tools deep-link here (?import=1) to open the ONE shared upload flow.
  useEffect(() => {
    if (params.get('import') === '1') { setShowImport(true); params.delete('import'); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const total = data?.total ?? 0;
  const shownFrom = page * PAGE;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="h-display text-3xl text-soft">Contacts</h1>
          <p className="mt-1 text-sm text-muted">Everyone you're working. Every touch logs itself.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/pipeline" className="text-sm font-semibold text-muted hover:text-soft">Board view</Link>
          <Button variant="ghost" onClick={() => setShowImport(true)}>Import CSV</Button>
          <Button onClick={() => setShowAdd(true)}>+ Add contact</Button>
        </div>
      </div>

      <ImportContacts open={showImport} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); reload(); }} />

      {batchId && (
        <Alert kind="info">
          Batch: <span className="font-semibold">{data?.batchName || '…'}</span>
          {data && <> — {data.total} contact{data.total === 1 ? '' : 's'}, {data.unscored} not yet scored</>}
          {' '}<button onClick={clearBatch} className="font-semibold underline">Show all contacts</button>
        </Alert>
      )}

      <input className="input max-w-sm" placeholder="Search name, business, email…" value={q} onChange={(e) => setQ(e.target.value)} />

      <ContactControls value={filters} onChange={setFilters} categories={data?.categories || []} stages={stages} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={toggleLock}
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${locked ? 'border-power bg-power/15 text-electric' : 'border-line text-muted hover:text-soft'}`}
          title={locked ? 'This filter/sort view is saved and reloads every visit' : 'Save this filter/sort view so it sticks every visit'}
        >
          {locked ? '🔒 View locked' : '🔓 Lock this view'}
        </button>
        {canScore && data && (
          <ScoreControls
            scoring={scorer.scoring}
            progress={scorer.progress}
            unscored={data.unscored}
            onAll={() => runScore('all')}
            onAmount={(n) => runScore(n)}
            label={batchId ? 'Score this batch' : 'Score view'}
          />
        )}
      </div>

      {error && <Alert>{error}</Alert>}
      {loading ? (
        <Loading label="Loading contacts…" />
      ) : !data || data.contacts.length === 0 ? (
        <EmptyState
          title={q || batchId || filters.tier || filters.category || filters.researched || filters.workStatus || filters.stage ? 'No matches' : 'No contacts yet'}
          body={q ? 'Try a different search or clear filters.' : 'Add your first contact, or source a batch from Lead Score.'}
          action={!q && <Button onClick={() => setShowAdd(true)}>+ Add your first contact</Button>}
        />
      ) : (
        <>
          <div className="flex items-center justify-between text-xs text-muted">
            <span>Showing {shownFrom + 1}–{shownFrom + data.contacts.length} of {total}</span>
          </div>
          <div className="card divide-y divide-line overflow-hidden">
            {data.contacts.map((c) => {
              const live = liveScores[c.id];
              const tier = live?.tier ?? c.tier;
              const score = live ? live.score : c.score;
              return (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-3">
                <Link to={`/contacts/${c.id}`} className="flex min-w-0 flex-1 items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-head font-semibold text-soft">
                      {c.businessName || c.contactName}
                      {c.companyName && c.contactName && <span className="ml-2 text-xs font-normal text-muted">{c.companyName}</span>}
                    </div>
                    <div className="truncate text-xs text-muted">{c.email || c.phone || c.category || '—'}</div>
                  </div>
                </Link>
                {c.grade
                  ? <Badge className={`border-line ${gradeColor(c.grade)}`} >🔎 {c.grade}</Badge>
                  : c.researched && <span className="hidden text-xs text-electric sm:inline" title="Deep Research done">🔎</span>}
                {tier ? (
                  <Badge className={`border-line ${tierColor(tier)}`} >
                    {tier}{score != null ? ` ${score}` : ''}
                    {tierGradeConflict(tier, c.grade) && <span title={`Scored ${tier} but researched grade ${c.grade} — grade is the deeper read`}> ⚠︎</span>}
                  </Badge>
                ) : canScore ? (
                  <button
                    onClick={() => scoreRow(c.id)}
                    disabled={scorer.scoring}
                    className="text-xs font-semibold text-electric hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
                    title="Score just this contact"
                  >
                    Score
                  </button>
                ) : null}
                <span className="hidden lg:block"><StageControl contactId={c.id} value={c.stageId} stages={stages} /></span>
                <WorkStatus contactId={c.id} value={c.workStatus || 'new'} />
                <span className="hidden w-16 text-right text-xs text-muted md:inline">{relTime(c.lastActivityAt)}</span>
              </div>
              );
            })}
          </div>
          {total > PAGE && (
            <div className="flex items-center justify-center gap-3">
              <Button variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← Prev</Button>
              <span className="text-xs text-muted">Page {page + 1} of {Math.ceil(total / PAGE)}</span>
              <Button variant="ghost" disabled={(shownFrom + data.contacts.length) >= total} onClick={() => setPage((p) => p + 1)}>Next →</Button>
            </div>
          )}
        </>
      )}

      <AddContactModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); reload(); }} />
    </div>
  );
}

function AddContactModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ businessName: '', contactName: '', email: '', phone: '', companyName: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dupWarn, setDupWarn] = useState<string | null>(null);
  const set = (p: Partial<typeof f>) => setF((prev) => ({ ...prev, ...p }));

  async function submit(e: FormEvent, force = false) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost('/api/contacts', { ...f, force });
      setF({ businessName: '', contactName: '', email: '', phone: '', companyName: '', website: '' });
      setDupWarn(null);
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setDupWarn('A contact with that email or phone already exists. Add anyway?');
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not add contact.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add a contact">
      <form onSubmit={(e) => submit(e)} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Business"><Input value={f.businessName} onChange={(e) => set({ businessName: e.target.value })} placeholder="Acme Co" /></Field>
          <Field label="Person"><Input value={f.contactName} onChange={(e) => set({ contactName: e.target.value })} placeholder="Jane Doe" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
          <Field label="Company (optional)"><Input value={f.companyName} onChange={(e) => set({ companyName: e.target.value })} /></Field>
          <Field label="Website (optional)"><Input value={f.website} onChange={(e) => set({ website: e.target.value })} /></Field>
        </div>
        {error && <Alert>{error}</Alert>}
        {dupWarn && (
          <Alert kind="info">
            {dupWarn}{' '}
            <button type="button" className="font-semibold underline" onClick={(e) => submit(e as any, true)}>Add anyway</button>
          </Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>Add contact</Button>
        </div>
      </form>
    </Modal>
  );
}
